/**
 * Web shim for @react-native-ml-kit/text-recognition.
 *
 * Uses Tesseract.js (WASM-based OCR) with aggressive image preprocessing
 * and tuned parameters for reading licence discs and VIN plates.
 *
 * The API surface mirrors the native package:
 *   TextRecognition.recognize(imageURL) → { text, blocks }
 */

import { logger } from '~/lib/logger';

// ─── Image helpers ──────────────────────────────────────────────────────────

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

// ─── Image preprocessing pipeline ──────────────────────────────────────────

/**
 * Compute Otsu's threshold — finds the optimal threshold to separate
 * foreground (text) from background, much better than a fixed 128 cutoff.
 */
function otsuThreshold(grayPixels: Uint8Array): number {
  // Build histogram
  const histogram = new Array<number>(256).fill(0);
  for (let i = 0; i < grayPixels.length; i++) {
    histogram[grayPixels[i]]++;
  }

  const total = grayPixels.length;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * histogram[i];

  let sumB = 0;
  let wB = 0;
  let maxVariance = 0;
  let threshold = 128;

  for (let i = 0; i < 256; i++) {
    wB += histogram[i];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;

    sumB += i * histogram[i];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const variance = wB * wF * (mB - mF) * (mB - mF);

    if (variance > maxVariance) {
      maxVariance = variance;
      threshold = i;
    }
  }
  return threshold;
}

/** Scale + draw the source image onto a canvas and return grayscale pixels. */
function prepareGrayCanvas(img: HTMLImageElement) {
  const canvas = document.createElement('canvas');
  const minTarget = 1500;
  const scale = Math.max(1, Math.min(3, minTarget / Math.min(img.width, img.height)));
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);

  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  const grayPixels = new Uint8Array(canvas.width * canvas.height);
  for (let i = 0; i < data.length; i += 4) {
    grayPixels[i / 4] = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
  }

  return {
    canvas,
    ctx,
    imageData,
    data,
    grayPixels,
    w: canvas.width,
    h: canvas.height,
  };
}

/** Add 20px white padding around a canvas and return as data URL. */
function padAndExport(canvas: HTMLCanvasElement): string {
  const pad = 20;
  const padded = document.createElement('canvas');
  padded.width = canvas.width + pad * 2;
  padded.height = canvas.height + pad * 2;
  const pCtx = padded.getContext('2d')!;
  pCtx.fillStyle = '#fff';
  pCtx.fillRect(0, 0, padded.width, padded.height);
  pCtx.drawImage(canvas, pad, pad);
  return padded.toDataURL('image/png');
}

/** Apply pixel values to canvas imageData and export with padding. */
function applyGrayAndExport(
  ctx: CanvasRenderingContext2D,
  imageData: ImageData,
  data: Uint8ClampedArray,
  pixels: Uint8Array,
  canvas: HTMLCanvasElement,
): string {
  for (let i = 0; i < pixels.length; i++) {
    const pi = i * 4;
    data[pi] = pixels[i];
    data[pi + 1] = pixels[i];
    data[pi + 2] = pixels[i];
    data[pi + 3] = 255;
  }
  ctx.putImageData(imageData, 0, 0);
  return padAndExport(canvas);
}

/**
 * Generate multiple preprocessed variants of the image.
 * Different VIN surfaces (embossed metal, printed sticker, licence disc)
 * respond better to different preprocessing — we try several and let the
 * OCR engine pick the best.
 */
async function generateImageVariants(
  imageURL: string,
): Promise<Array<{ name: string; url: string }>> {
  logger.log('[TextRecognition Web] Generating image variants...');
  const img = await loadImage(imageURL);
  logger.log(`[TextRecognition Web] Source: ${img.width}×${img.height}`);

  const variants: Array<{ name: string; url: string }> = [];

  // ── Variant 1: Grayscale (no binarisation) ────────────────────────────
  // Best for printed text on coloured backgrounds (licence discs).
  {
    const { canvas, ctx, imageData, data, grayPixels } = prepareGrayCanvas(img);
    variants.push({
      name: 'grayscale',
      url: applyGrayAndExport(ctx, imageData, data, grayPixels, canvas),
    });
  }

  // ── Variant 2: High-contrast grayscale ────────────────────────────────
  // Stretches histogram to full 0–255 range. Good for washed-out images.
  {
    const { canvas, ctx, imageData, data, grayPixels } = prepareGrayCanvas(img);
    let min = 255,
      max = 0;
    for (let i = 0; i < grayPixels.length; i++) {
      if (grayPixels[i] < min) min = grayPixels[i];
      if (grayPixels[i] > max) max = grayPixels[i];
    }
    const range = max - min || 1;
    const stretched = new Uint8Array(grayPixels.length);
    for (let i = 0; i < grayPixels.length; i++) {
      stretched[i] = Math.round(((grayPixels[i] - min) / range) * 255);
    }
    variants.push({
      name: 'contrast',
      url: applyGrayAndExport(ctx, imageData, data, stretched, canvas),
    });
  }

  // ── Variant 3: Otsu binarised ─────────────────────────────────────────
  // Best for high-contrast printed text.
  {
    const { canvas, ctx, imageData, data, grayPixels } = prepareGrayCanvas(img);
    const threshold = otsuThreshold(grayPixels);
    const binarised = new Uint8Array(grayPixels.length);
    for (let i = 0; i < grayPixels.length; i++) {
      binarised[i] = grayPixels[i] > threshold ? 255 : 0;
    }
    variants.push({
      name: 'otsu',
      url: applyGrayAndExport(ctx, imageData, data, binarised, canvas),
    });
  }

  // ── Variant 4: Inverted (white text on dark → dark text on white) ─────
  // Embossed/engraved VIN plates are often light text on dark metal.
  {
    const { canvas, ctx, imageData, data, grayPixels } = prepareGrayCanvas(img);
    const inverted = new Uint8Array(grayPixels.length);
    for (let i = 0; i < grayPixels.length; i++) {
      inverted[i] = 255 - grayPixels[i];
    }
    variants.push({
      name: 'inverted',
      url: applyGrayAndExport(ctx, imageData, data, inverted, canvas),
    });
  }

  logger.log(`[TextRecognition Web] Generated ${variants.length} variants`);
  return variants;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface TextRecognitionResult {
  text: string;
  blocks: Array<{
    text: string;
    frame: { left: number; top: number; width: number; height: number };
    lines: Array<{
      text: string;
      frame: { left: number; top: number; width: number; height: number };
    }>;
  }>;
}

export enum TextRecognitionScript {
  LATIN = 'Latin',
  CHINESE = 'Chinese',
  DEVANAGARI = 'Devanagari',
  JAPANESE = 'Japanese',
  KOREAN = 'Korean',
}

// ─── Helpers ────────────────────────────────────────────────────────────────

type TesseractModule = typeof import('tesseract.js');

async function createConfiguredWorker(
  Tesseract: TesseractModule,
): Promise<import('tesseract.js').Worker> {
  const worker = await Tesseract.createWorker('eng');
  await worker.setParameters({
    tessedit_pageseg_mode: Tesseract.PSM.SINGLE_BLOCK,
    user_defined_dpi: '300',
    preserve_interword_spaces: '1',
  });
  return worker;
}

/**
 * Map Tesseract.js v7 Page → ML Kit-compatible { text, blocks } structure.
 * v7 uses Page.blocks[] → Block.paragraphs[] → Paragraph.lines[].
 */
function mapPage(page: import('tesseract.js').Page): TextRecognitionResult['blocks'] {
  const tessBlocks = page.blocks ?? [];
  // Flatten: each Tesseract Block can have multiple paragraphs — we map each
  // paragraph as a separate ML-Kit-style "block" (which is what the native
  // @react-native-ml-kit/text-recognition returns).
  return tessBlocks.flatMap((block) =>
    (block.paragraphs ?? []).map((p) => ({
      text: p.text,
      frame: {
        left: p.bbox.x0,
        top: p.bbox.y0,
        width: p.bbox.x1 - p.bbox.x0,
        height: p.bbox.y1 - p.bbox.y0,
      },
      lines: (p.lines ?? []).map((l) => ({
        text: l.text,
        frame: {
          left: l.bbox.x0,
          top: l.bbox.y0,
          width: l.bbox.x1 - l.bbox.x0,
          height: l.bbox.y1 - l.bbox.y0,
        },
      })),
    })),
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

/** Score OCR text for VIN-likeness. Higher = more likely to contain a VIN. */
function vinScore(text: string): number {
  const upper = text.toUpperCase();
  let score = 0;

  // Strong signal: VIN label present
  if (/VIN\s*[:\-.]?\s*[A-HJ-NPR-Z0-9]{5,}/i.test(upper)) score += 100;

  // 17-char VIN candidate in the text (exact match)
  const stripped = upper.replace(/[^A-HJ-NPR-Z0-9]/g, '');
  for (let i = 0; i <= stripped.length - 17; i++) {
    const candidate = stripped.substring(i, i + 17);
    const digits = candidate.replace(/[^0-9]/g, '').length;
    const letters = candidate.replace(/[^A-Z]/g, '').length;
    if (digits >= 2 && letters >= 2) {
      score += 50;
      break;
    }
  }

  // Alphanumeric content ratio (higher = less garbage)
  const alphaNum = upper.replace(/[^A-Z0-9]/g, '').length;
  score += Math.min(30, Math.round((alphaNum / Math.max(1, upper.length)) * 30));

  return score;
}

const TextRecognition = {
  recognize: async (
    imageURL: string,
    _script = TextRecognitionScript.LATIN,
  ): Promise<TextRecognitionResult> => {
    logger.log('[TextRecognition Web] recognize() called');

    // Generate multiple preprocessed image variants
    let variants: Array<{ name: string; url: string }>;
    try {
      variants = await generateImageVariants(imageURL);
    } catch (err) {
      logger.error('[TextRecognition Web] Image preprocessing failed:', err);
      throw new Error('Failed to read image for text recognition.');
    }

    try {
      const Tesseract = await import('tesseract.js');

      // Run OCR on all variants in parallel (each with its own worker)
      const results = await Promise.allSettled(
        variants.map(async (variant) => {
          const worker = await createConfiguredWorker(Tesseract);
          try {
            // Pass A: full character set
            const { data: fullPass } = await worker.recognize(variant.url);

            // Pass B: VIN-restricted whitelist
            await worker.setParameters({
              tessedit_char_whitelist: 'ABCDEFGHJKLMNPRSTUVWXYZ0123456789 .:/-',
            });
            const { data: vinPass } = await worker.recognize(variant.url);

            return { variant: variant.name, fullPass, vinPass };
          } finally {
            await worker.terminate();
          }
        }),
      );

      // Collect all successful texts and pick the best
      let bestScore = -1;
      let bestPage: import('tesseract.js').Page | null = null;
      const allTexts: string[] = [];

      for (const r of results) {
        if (r.status !== 'fulfilled') continue;
        const { variant, fullPass, vinPass } = r.value;

        // Score both passes for this variant
        for (const [passName, page] of [
          ['full', fullPass],
          ['vin', vinPass],
        ] as const) {
          const score = vinScore(page.text);
          logger.log(
            `[TextRecognition Web] ${variant}/${passName}: score=${score}, ` +
              `text=${page.text.substring(0, 120).replace(/\n/g, '\\n')}`,
          );
          allTexts.push(page.text);
          if (score > bestScore) {
            bestScore = score;
            bestPage = page;
          }
        }
      }

      if (!bestPage) {
        throw new Error('All OCR variants failed.');
      }

      // Merge all texts so the VIN extractor has every variant's output
      const merged = allTexts.join('\n---\n');
      logger.log(`[TextRecognition Web] Best: score=${bestScore}, merged length=${merged.length}`);

      return { text: merged, blocks: mapPage(bestPage) };
    } catch (err) {
      logger.error('[TextRecognition Web] OCR failed:', err);
      throw new Error(
        'Text recognition failed. Please try again or enter the VIN number manually.',
      );
    }
  },
};

export default TextRecognition;
